// Toast stack (§11.5, §14.2): short notices (join, leave, host change, takeover, challenge
// complete, level up, graphics tier, errors) dropping from the top like stickers, each with an
// icon and color per kind, leaving with a little hop. Duplicates within a moment are merged.
import { RoomPhase } from '@shared/ids';
import type { UiDeps } from '../deps';
import { icon } from '../icons';
import { el } from '../ui';

const TOAST_MS = 3400;
const OUT_MS = 260;
const MAX_TOASTS = 4;

const KIND: Readonly<Record<string, { cls: string; icon: string }>> = {
  join: { cls: 'good', icon: 'plus' },
  leave: { cls: 'warn', icon: 'exit' },
  host: { cls: 'host', icon: 'crown' },
  takeover: { cls: 'sky', icon: 'bot' },
  challenge: { cls: 'good', icon: 'flag' },
  levelup: { cls: 'host', icon: 'star' },
  error: { cls: 'bad', icon: 'x' },
  warn: { cls: 'warn', icon: 'info' },
  info: { cls: '', icon: 'info' },
};

/** Kinds the podium's rewards panel already celebrates (LEVEL UP!, "n challenges done"). */
const PODIUM_SHOWN = new Set(['levelup', 'challenge']);

/**
 * True when a toast is left to the podium's rewards panel: match-end rewards (level up, challenges
 * completed by the final standings) arrive with `matchEnd`, during AWARDS and PODIUM.
 */
export function leftToPodium(kind: string, phase: RoomPhase | null, matchEnded: boolean): boolean {
  return PODIUM_SHOWN.has(kind) && (matchEnded || phase === RoomPhase.AWARDS || phase === RoomPhase.PODIUM);
}

export function createToasts(deps: Pick<UiDeps, 'bus' | 'services' | 'room'>): HTMLElement {
  const box = el('div', { class: 'toasts', attrs: { 'aria-live': 'polite' } });
  const recent = new Map<string, number>();
  function show(text: string, kind: string): void {
    const now = performance.now();
    const k = `${kind}:${text}`;
    if ((recent.get(k) ?? -Infinity) > now - 1200) return;
    recent.set(k, now);
    const style = KIND[kind] ?? KIND.info;
    const node = el('div', { class: `toast ${style.cls}`, attrs: { role: 'status' } }, el('span', { class: 'toast-ico' }, icon(style.icon)), el('span', { class: 'toast-text', text }));
    box.append(node);
    deps.services.playUi('toast');
    while (box.childElementCount > MAX_TOASTS) box.firstElementChild?.remove();
    setTimeout(() => {
      node.classList.add('out');
      setTimeout(() => node.remove(), OUT_MS);
    }, TOAST_MS);
  }
  deps.bus.on('toast', ({ text, kind }) => {
    if (!PODIUM_SHOWN.has(kind)) {
      show(text, kind);
      return;
    }
    // Match-end rewards land with `matchEnd`: the podium's rewards panel shows them, so no toast
    // covers the Play of the Match title, the award cards or the podium banner. Checked after the
    // current dispatch, whichever listener heard `match:end` first.
    queueMicrotask(() => {
      if (!leftToPodium(kind, deps.room.phase, deps.room.matchEnd !== null)) show(text, kind);
    });
  });
  return box;
}
